<script lang="ts">import ActionIcon from './ActionIcon/ActionIcon.svelte';
const { as = 'a', variant = 'button', colorMode, size = 'regular', disabled = false, href, altText = '', target, rel, balanced = false, isActive = false, onClick = () => { }, modal, noArrow = false, preventOrphanIcon = false, text = '', class: className = '', ...rest } = $props();
let textLastWord = $state('');
let textExceptLastWord = $state(text);
function setSplitText(text, preventOrphanIcon) {
    if (text && preventOrphanIcon) {
        // preventOrphanIcon does not work without calling trim because the split is preserving spaces, so 'Learn more '
        // is actually ['Learn', ' ', 'more', ' '] and that means textLast word would be an empty space
        const trimmedText = text.trim();
        const textWords = trimmedText.split(/(\s+)/);
        textLastWord = textWords.pop() || '';
        textExceptLastWord = textWords.join('');
    }
    else {
        textExceptLastWord = text ?? '';
    }
}
$effect(() => {
    if (text || preventOrphanIcon) {
        setSplitText(text, preventOrphanIcon);
    }
});
function handleOnClick(event) {
    if (modal) {
        event.preventDefault();
    }
    onClick?.(event);
}
// All the variants have an icon except button, button-alt, and text when noArrow is true
const isButtonOrButtonAltVariant = $derived(['button', 'button-alt'].includes(variant));
// The variant is forced to be modal if a modal component is added AND if button/button-alt is not used even if it may
// not be selected as the variant
const finalVariant = $derived(modal && !isButtonOrButtonAltVariant ? 'modal' : variant);
const renderIcon = $derived(!(isButtonOrButtonAltVariant || (finalVariant === 'text' && noArrow)) || (modal && isButtonOrButtonAltVariant));
</script>

{#snippet actionText()}
  {textExceptLastWord}
  <span class="Action__no-wrap">
    {textLastWord}
    {#if renderIcon}
      <span class="Action__icon">
        <ActionIcon variant={finalVariant} />
      </span>
    {/if}
  </span>
{/snippet}

<svelte:element
  this={as}
  {...rest}
  class="Action Action--{finalVariant} Action--{size} {className}"
  class:Action--balanced={balanced}
  class:Action--is-active={isActive}
  data-color-mode={colorMode}
  {...as === 'a' ? { 'aria-disabled': disabled } : { disabled }}
  {href}
  title={altText || undefined}
  {target}
  {rel}
  onclick={handleOnClick}
  style:--action-background-color="var(--action-variant-{finalVariant}-background-color)"
  style:--action-text-color="var(--action-variant-{finalVariant}-text-color)"
  style:--action-border-color="var(--action-variant-{finalVariant}-border-color)"
>
  <!-- Using content-wrapper to free up pseudo selectors for anchor/button -->
  <span class="Action__content-wrapper">
    {#if isButtonOrButtonAltVariant}
      <span class="Action__content">
        {@render actionText()}
      </span>
    {:else}
      {@render actionText()}
    {/if}
  </span>
</svelte:element>

<style>.Action--anchor .Action__content-wrapper, .Action--button .Action__content-wrapper:before, .Action--button-alt .Action__content-wrapper:before {
  background-color: var(--action-background-color);
  border: var(--border-line-width) var(--border-line-style) var(--action-border-color);
  border-radius: var(--action-variant-button-border-radius);
}

@layer design-system-components.pwds {
  .Action {
    position: static;
    display: inline-block;
    text-decoration: none;
    cursor: pointer;
  }
  .Action[aria-disabled=true], .Action:disabled {
    pointer-events: none;
    opacity: var(--color-action-opacity);
  }
  .Action .Action__content-wrapper {
    position: relative;
    --font-weight: var(--text-font-weight-medium);
    font-weight: var(--font-weight);
    color: var(--action-text-color);
  }
  .Action:focus-visible:not(:active) {
    outline: none;
  }
  .Action:focus-visible:not(:active) .Action__content-wrapper {
    outline: var(--accessibility-focus-outline-width) var(--accessibility-focus-outline-style, solid) var(--accessibility-focus-outline-color);
    border-radius: var(--accessibility-focus-outline-border-radius);
    outline-offset: var(--accessibility-focus-outline-offset);
  }
  .Action:focus-visible:not(:active).Action--button .Action__content-wrapper, .Action:focus-visible:not(:active).Action--button-alt .Action__content-wrapper, .Action:focus-visible:not(:active).Action--anchor .Action__content-wrapper {
    outline: var(--accessibility-focus-outline-width) var(--accessibility-focus-outline-style, solid) var(--accessibility-focus-outline-color);
    border-radius: var(--action-variant-button-border-radius);
    outline-offset: var(--accessibility-focus-outline-offset);
  }
  .Action--button, .Action--button-alt, .Action--anchor {
    appearance: none;
  }
  .Action--button .Action__content-wrapper, .Action--button-alt .Action__content-wrapper, .Action--anchor .Action__content-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--action-variant-button-padding, var(--action-variant-button-size-regular-padding));
  }
  .Action--button,
  .Action--button .Action__content-wrapper, .Action--button-alt,
  .Action--button-alt .Action__content-wrapper, .Action--anchor,
  .Action--anchor .Action__content-wrapper {
    font-size: var(--action-variant-button-font-size, var(--action-size-regular-font-size));
    line-height: var(--action-variant-button-line-height);
    border-radius: var(--action-variant-button-border-radius);
    text-align: center;
  }
  .Action--button .Action__content-wrapper, .Action--button-alt .Action__content-wrapper {
    min-width: var(--action-variant-button-min-width, var(--action-variant-button-size-regular-min-width));
  }
  .Action--button .Action__content-wrapper:before, .Action--button-alt .Action__content-wrapper:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    transform: scale(1);
    transition: transform var(--duration-quick) var(--easing-decelerate);
  }
  .Action--button .Action__content-wrapper, .Action--button:hover .Action__content-wrapper, .Action--button:focus .Action__content-wrapper, .Action--button-alt .Action__content-wrapper, .Action--button-alt:hover .Action__content-wrapper, .Action--button-alt:focus .Action__content-wrapper {
    transition: transform var(--duration-quick) var(--easing-decelerate);
  }
  .Action--button:hover .Action__content-wrapper:before, .Action--button-alt:hover .Action__content-wrapper:before {
    transform: scale(var(--action-variant-button-hover-scale));
  }
  .Action--button:active .Action__content-wrapper, .Action--button-alt:active .Action__content-wrapper {
    transform: translateY(var(--action-variant-button-active-translate-y));
  }
  .Action--button.Action--small,
  .Action--button.Action--small .Action__content-wrapper, .Action--button-alt.Action--small,
  .Action--button-alt.Action--small .Action__content-wrapper {
    --action-variant-button-min-width: var(--action-variant-button-size-small-min-width);
    --action-variant-button-padding: var(--action-variant-button-size-small-padding);
    --action-variant-button-font-size: var(--action-size-small-font-size);
  }
  .Action--button.Action--regular,
  .Action--button.Action--regular .Action__content-wrapper, .Action--button.Action--large,
  .Action--button.Action--large .Action__content-wrapper, .Action--button-alt.Action--regular,
  .Action--button-alt.Action--regular .Action__content-wrapper, .Action--button-alt.Action--large,
  .Action--button-alt.Action--large .Action__content-wrapper {
    --action-variant-button-min-width: var(--action-variant-button-size-regular-min-width);
    --action-variant-button-padding: var(--action-variant-button-size-regular-padding);
    --action-variant-button-font-size: var(--action-size-regular-font-size);
  }
  .Action--text .Action__content-wrapper, .Action--video .Action__content-wrapper, .Action--modal .Action__content-wrapper {
    display: inline-block;
  }
  .Action--text,
  .Action--text .Action__content-wrapper, .Action--video,
  .Action--video .Action__content-wrapper, .Action--modal,
  .Action--modal .Action__content-wrapper {
    --action-variant-text-text-color: var(--color-text, var(--color-mode-light-text));
    --font-size: var(--action-variant-text-font-size, var(--action-size-regular-font-size));
    --line-height: var(--action-variant-text-line-height, var(--action-variant-text-size-regular-line-height));
    font-weight: var(--font-weight);
    font-size: var(--font-size);
    line-height: var(--line-height);
  }
  .Action--text.Action--small,
  .Action--text.Action--small .Action__content-wrapper, .Action--video.Action--small,
  .Action--video.Action--small .Action__content-wrapper, .Action--modal.Action--small,
  .Action--modal.Action--small .Action__content-wrapper {
    --action-variant-text-font-size: var(--action-size-small-font-size);
    --action-variant-text-line-height: var(--action-variant-text-size-small-line-height);
  }
  .Action--text.Action--regular,
  .Action--text.Action--regular .Action__content-wrapper, .Action--video.Action--regular,
  .Action--video.Action--regular .Action__content-wrapper, .Action--modal.Action--regular,
  .Action--modal.Action--regular .Action__content-wrapper {
    --action-variant-text-font-size: var(--action-size-regular-font-size);
    --action-variant-text-line-height: var(--action-variant-text-size-regular-line-height);
  }
  .Action--text.Action--large,
  .Action--text.Action--large .Action__content-wrapper, .Action--video.Action--large,
  .Action--video.Action--large .Action__content-wrapper, .Action--modal.Action--large,
  .Action--modal.Action--large .Action__content-wrapper {
    --action-variant-text-font-size: var(--action-size-large-font-size);
    --action-variant-text-line-height: var(--action-variant-text-size-large-line-height);
  }
  .Action--text.Action--balanced,
  .Action--text.Action--balanced .Action__content-wrapper, .Action--video.Action--balanced,
  .Action--video.Action--balanced .Action__content-wrapper, .Action--modal.Action--balanced,
  .Action--modal.Action--balanced .Action__content-wrapper {
    text-wrap: balance;
  }
  .Action--text .Action__icon > :global(.ActionIcon__ArrowAngled) {
    transition: transform var(--duration-quick) var(--easing-decelerate);
    transform: translate3d(0, 0, 0);
  }
  .Action--text:hover .Action__icon > :global(.ActionIcon__ArrowAngled) {
    transform: translate3d(30%, -30%, 0);
  }
  .Action--video .Action__icon > :global(.ActionIcon__Video *) {
    transition: fill var(--duration-quick) var(--easing-decelerate), filter var(--duration-quick) var(--easing-decelerate);
  }
  .Action--video:hover .Action__icon > :global(.ActionIcon__Video *) {
    --action-icon-video-circle-fill: transparent;
    --action-icon-video-path-fill: currentColor;
    --action-icon-video-path-filter: none;
  }
  .Action--anchor .Action__content-wrapper {
    --action-icon-arrow-down-size: var(--action-variant-button-font-size);
    filter: invert(0%);
    transition: filter var(--duration-quick) var(--easing-decelerate);
  }
  .Action--anchor.Action--is-active .Action__content-wrapper, .Action--anchor:hover .Action__content-wrapper, .Action--anchor:active .Action__content-wrapper {
    filter: invert(100%);
  }
  .Action--anchor.Action--small,
  .Action--anchor.Action--small .Action__content-wrapper, .Action--anchor.Action--regular,
  .Action--anchor.Action--regular .Action__content-wrapper, .Action--anchor.Action--large,
  .Action--anchor.Action--large .Action__content-wrapper {
    --action-variant-button-min-width: var(--action-variant-button-size-small-min-width);
    --action-variant-button-padding: var(--action-variant-button-size-small-padding);
    --action-variant-button-font-size: var(--action-size-small-font-size);
  }
  .Action__content, .Action__no-wrap, .Action__icon {
    font-size: inherit;
    line-height: inherit;
    color: inherit;
  }
  .Action__content {
    position: relative;
  }
  .Action__no-wrap {
    white-space: nowrap;
  }
  .Action__icon {
    display: inline-block;
    margin-left: var(--action-icon-gap);
    vertical-align: baseline;
    white-space: nowrap;
  }
  .Action__icon :global(*) {
    color: currentColor;
  }
  .Action--anchor .Action__icon {
    --action-icon-gap: var(--space-10);
  }
}</style>
